/**
 * @file VendorList.js
 * @description Component that displays a list of vendors with search, sort, filter, and pagination functionality.
 */
import React, { useState, useEffect, useContext } from 'react';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import Toast from '../ui/toasts/Toast';
import PaginationComponent from '../helpers/PaginationComponent';
import { fetchVendors, deleteVendorById } from '../../services/systemService';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useNavigate } from 'react-router';
import { VendorCategoryEnum, LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import styles from './Vendors.module.scss';
import './VendorList.scss';
import '../page-list/PageList.css';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const getVendorCategoryLabel = vendorCategoryId => {
    return VendorCategoryEnum[vendorCategoryId] || '';
};

const VendorList = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const { showModal } = useContext(ModalContext);
    const [allVendors, setAllVendors] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [rowsClicked, setRowsClicked] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});

    const { hasAttribute } = useAuthContext();

    const pageSize = 10;

    const canEditForm = hasAttribute(SecurityAttributes.VendorFullAccess);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
        setSortConfig({
            key: 'name',
            direction: SortDirections.ASCENDING,
        });
        setRowsClicked([]);
        setCurrentPage(1);
    };


    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filtered = allVendors.filter(vendor =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(vendor[key]),
            ),
        );

        return Array.from(new Set(filtered.map(item => item[column])))
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    useEffect(() => {
        const searchFiltered = allVendors.filter(vendor => {
            const code = (vendor.code || '').toString().toLowerCase();
            const name = (vendor.name || '').toString().toLowerCase();
            const categoryLabel = (
                VendorCategoryEnum[vendor.vendorCategoryId] || ''
            ).toLowerCase();
            const normalizedTerm = searchTerm.toLowerCase();

            return (
                code.includes(normalizedTerm) ||
                name.includes(normalizedTerm) ||
                categoryLabel.includes(normalizedTerm)
            );
        });
        setBaseFilteredData(searchFiltered);
        setCurrentPage(1);
    }, [searchTerm, allVendors]);

    const sortedAndPaginatedVendors = React.useMemo(() => {
        const sorted = [...filteredData].sort((a, b) => {
            const aValue = (a[sortConfig.key] || '').toString().toLowerCase();
            const bValue = (b[sortConfig.key] || '').toString().toLowerCase();

            if (sortConfig.direction === SortDirections.ASCENDING) {
                return aValue.localeCompare(bValue);
            }
            return bValue.localeCompare(aValue);
        });

        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sorted.slice(startIndex, endIndex);
    }, [filteredData, sortConfig, currentPage]);

    useEffect(() => {
        setTotalPages(Math.max(1, Math.ceil(filteredData.length / pageSize)));
    }, [filteredData]);

    const handleSortVendorCode = () => {
        setSortConfig({
            key: 'code',
            direction:
                sortConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        });
    };

    const handleSortVendorName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'name',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'name',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handleSortVendorCategory = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'category',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'category',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handlerRowClicked = vendorId => {
        setRowsClicked(prevSelection =>
            prevSelection.includes(vendorId)
                ? prevSelection.filter(id => id !== vendorId)
                : [...prevSelection, vendorId],
        );
    };

    const handleDeleteVendor = async () => {
        try {
            await Promise.all(
                rowsClicked.map(vendorId => deleteVendorById(vendorId)),
            );
            Toast.success(
                `${rowsClicked.length} vendor(s) deleted successfully`,
            );
            loadVendors();
            setRowsClicked([]);
        } catch (error) {
            console.error('Failed to delete vendor(s).', error);
            Toast.error(getText('msg_0c9d92fbe486'));
        }
    };

    const openDeleteConfirmationModal = () => {
        if (!rowsClicked.length) {
            Toast.error(getText('msg_c8ccc5b97e0d'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            onYes: handleDeleteVendor,
            onNo: () => { },
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Vendor delete clicked`);
    };

    const loadVendors = async () => {
        try {
            const fetchedVendors = await fetchVendors();
            setAllVendors(Array.isArray(fetchedVendors) ? fetchedVendors : []);
            setBaseFilteredData(
                Array.isArray(fetchedVendors) ? fetchedVendors : [],
            );
        } catch (error) {
            console.error('Failed to get Vendors.', error);
            Toast.error(getText('msg_12a7d3c052bc'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    useEffect(() => {
        loadVendors();
    }, []);

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content list-vendors">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="float-start d-flex align-items-center">
                            <p className="page-title card-title ms-4 me-5">
                                {getText('msg_634d89417f97')}
                            </p>
                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={getText('msg_c8884c520530')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <span className={styles.searchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>
                        </div>
                        <div className="float-end d-flex align-items-center">
                            {canEditForm && (
                                <span
                                    type="button"
                                    onClick={openDeleteConfirmationModal}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                            )}
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            {canEditForm && (
                                <span
                                    onClick={() => navigate(`/vendors/add`)}
                                    type="button"
                                    className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    {canEditForm && (
                                        <th className="text-center vendor-list-fixed-width">
                                            <svg
                                                width="19"
                                                height="19"
                                                viewBox="0 0 19 19"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M5.2 9.3H12.8"
                                                    stroke="#0070FF"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <rect
                                                    x="0.5"
                                                    y="0.5"
                                                    width="17"
                                                    height="17"
                                                    rx="4"
                                                    stroke="#0070FF"
                                                />
                                            </svg>
                                        </th>
                                    )}
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <span
                                            onClick={handleSortVendorName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('name');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['name']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_adac69379a62')}
                                        </span>
                                        <span
                                            onClick={handleSortVendorCode}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'code' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('code');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['code']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'code' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_a3c686e711e4')}
                                        </span>
                                        <span
                                            onClick={handleSortVendorCategory}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'vendorCategoryId' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('vendorCategoryId');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['vendorCategoryId']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'vendorCategoryId' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                getString={
                                                    getVendorCategoryLabel
                                                }
                                            />
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAndPaginatedVendors.map(vendor => (
                                    <tr
                                        key={vendor.id}
                                        className={`${rowsClicked.includes(vendor.id)
                                            ? 'clicked-class'
                                            : ''
                                            }`}
                                    >
                                        {canEditForm && (
                                            <td className="text-center">
                                                <input
                                                    id={vendor.id}
                                                    checked={rowsClicked.includes(
                                                        vendor.id,
                                                    )}
                                                    onChange={() =>
                                                        handlerRowClicked(vendor.id)
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                />
                                            </td>
                                        )}
                                        <td
                                            className="titleCell"
                                            onClick={() =>
                                                navigate(
                                                    `/vendors/edit/${vendor.id}`,
                                                )
                                            }
                                        >
                                            {vendor.name}
                                        </td>
                                        <td className="subtitleCell">
                                            {vendor.code}
                                        </td>
                                        <td className="subtitleCell">
                                            {getVendorCategoryLabel(
                                                vendor.vendorCategoryId,
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {sortedAndPaginatedVendors.length < 1 && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_d15bf09755d4')}
                                description={getText('msg_5fa26d916809')}
                            />
                        )}
                        {totalPages > 1 && (
                            <div className="mt-auto">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorList;
